import React, { type JSX } from 'react';

import { getApiBase } from '../Env/apiBase';
import { determineEnvironmentFromApiBase } from '../Env/rover';

import { useRoverStatsigUser } from './hooks/useRoverStatsigUser';
import { useStatsigUserUpdates } from './hooks/useStatsigUserUpdates';
import { getStatsigConfig, StatsigPlatform } from './config';
import { StatsigProvider, useClientAsyncInit, useClientBootstrapInit } from './StatsigLib';
import type { RoverStatsigUser, StatsigOptions } from './types';

export type Props = {
  children: React.ReactNode;
  /** Optional user attributes to initialize Statsig, avoiding an API call if provided. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialUserAttributes?: Record<string, any>;
};

type ProviderProps = {
  children: React.ReactNode;
  apiKey: string;
  clientOptions: StatsigOptions;
  roverStatsigUser: RoverStatsigUser;
};

/** Uses bootstrap values from SSR to prevent flickering on initial render. */
const BootstrapProvider = ({
  children,
  apiKey,
  clientOptions,
  roverStatsigUser,
  bootstrapValues,
}: ProviderProps & { bootstrapValues: string }): JSX.Element => {
  const client = useClientBootstrapInit(
    apiKey,
    roverStatsigUser,
    bootstrapValues,
    // Disable the client-managed StableID so the browser client matches the
    // server, which computes the bootstrap values with `disableStableID: true`
    // (see generateStatsigBootstrap.ts). Randomization is keyed on `rover_cid`,
    // not Statsig's StableID; leaving it enabled makes the client generate its
    // own StableID that diverges from the bootstrap, producing the
    // `BootstrapStableIDMismatch` evaluation reason on every SSR page.
    { ...clientOptions, disableStableID: true },
    false // useLegacyFormat - set to false for new djb2 hash format
  );

  useStatsigUserUpdates(client, roverStatsigUser);

  return <StatsigProvider client={client}>{children}</StatsigProvider>;
};

/** Falls back to async initialization when no bootstrap data is available. */
const AsyncProvider = ({
  children,
  apiKey,
  clientOptions,
  roverStatsigUser,
}: ProviderProps): JSX.Element => {
  const { client } = useClientAsyncInit(apiKey, roverStatsigUser, clientOptions);

  useStatsigUserUpdates(client, roverStatsigUser);

  return <StatsigProvider client={client}>{children}</StatsigProvider>;
};

const RoverStatsigProvider = ({ children, initialUserAttributes }: Props): JSX.Element => {
  const { roverStatsigUser } = useRoverStatsigUser(initialUserAttributes);

  const apiBase = getApiBase();
  const apiEnvironmentName = determineEnvironmentFromApiBase(apiBase);
  const { apiKey, clientOptions } = getStatsigConfig(apiEnvironmentName, StatsigPlatform.REACT);

  // Get bootstrap values from window (injected server-side during SSR)
  // eslint-disable-next-line no-underscore-dangle
  const bootstrapValues = process.env.JS_ENV_CLIENT ? window.__STATSIG_BOOTSTRAP__ : undefined;

  // When bootstrap data is available (SSR), use bootstrap init to prevent flickering.
  // Otherwise, fall back to async init which fetches values from the network.
  // https://docs.statsig.com/client/concepts/initialize#2-bootstrap-initialization
  if (bootstrapValues) {
    return (
      <BootstrapProvider
        apiKey={apiKey}
        clientOptions={clientOptions}
        roverStatsigUser={roverStatsigUser}
        bootstrapValues={bootstrapValues}
      >
        {children}
      </BootstrapProvider>
    );
  }

  return (
    <AsyncProvider
      apiKey={apiKey}
      clientOptions={clientOptions}
      roverStatsigUser={roverStatsigUser}
    >
      {children}
    </AsyncProvider>
  );
};

export default RoverStatsigProvider;
